/**
 * Generator Hook
 * Manages state for running the AI pick generator from the Lab view
 */

import { useAuth } from '@contexts/AuthContext';
import { useCurrentLeagueKey } from '@hooks/useCurrentLeagueKey';
import type { AnalysisMode } from '@parlay-picker/shared/analysisMode';
import { sportsbookForLeague } from '@parlay-picker/shared/sportsbook';
import { useCallback, useEffect, useRef, useState } from 'react';

// =============================================================================
// Constants
// =============================================================================

/** Delay between loop iterations in milliseconds (45 seconds) */
const LOOP_DELAY_MS = 45000;

/** Session storage key for persisting generator results */
const RESULTS_STORAGE_KEY = 'lab-generator-results';

/** Session storage key for persisting loop state */
const LOOP_STATE_STORAGE_KEY = 'lab-generator-loop-state';

/** Session storage key for persisting selected config */
const SELECTED_CONFIG_STORAGE_KEY = 'lab-generator-selected-config';

/**
 * Load results from sessionStorage
 */
function loadPersistedResults(): GeneratorResult[] {
  try {
    const stored = sessionStorage.getItem(RESULTS_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (_err) {}
  return [];
}

/**
 * Save results to sessionStorage
 */
function persistResults(results: GeneratorResult[]): void {
  try {
    // Keep only the last 50 results to avoid storage limits
    const toStore = results.slice(0, 50);
    sessionStorage.setItem(RESULTS_STORAGE_KEY, JSON.stringify(toStore));
  } catch (_err) {}
}

interface PersistedLoopState {
  loopRunning: boolean;
  loopProgress: { current: number; total: number } | null;
  selectedConfig: string | null;
}

/**
 * Load loop state from sessionStorage
 */
function loadPersistedLoopState(): PersistedLoopState | null {
  try {
    const stored = sessionStorage.getItem(LOOP_STATE_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (_err) {}
  return null;
}

/**
 * Save loop state to sessionStorage
 */
function persistLoopState(state: PersistedLoopState | null): void {
  try {
    if (state?.loopRunning) {
      sessionStorage.setItem(LOOP_STATE_STORAGE_KEY, JSON.stringify(state));
    } else {
      // Clear when not running
      sessionStorage.removeItem(LOOP_STATE_STORAGE_KEY);
    }
  } catch (_err) {}
}

// =============================================================================
// Types
// =============================================================================

export interface ConfigToggles {
  // Player Stats
  l5Average: boolean;
  l10Average: boolean;
  l20Average: boolean;
  seasonAverage: boolean;
  // Game Script
  blowoutRisk: boolean;
  backToBack: boolean;
  paceImpact: boolean;
  gameTotal: boolean;
  // Other Factors
  trendAnalysis: boolean;
  injuryStatus: boolean;
  // Matchup Data
  playerVsTeamHistory: boolean;
  playerPosition: boolean;
  // Opponent Defense
  defVsPosition: boolean;
}

export interface GeneratorConfig {
  name: string;
  gameCount: number | 'all';
  category: string;
  toggles: ConfigToggles;
  propTypes: string[] | null;
}

export interface GeneratorGame {
  id: string;
  homeTeam: string;
  awayTeam: string;
  gameStartsAt: string;
}

export interface GeneratorResult {
  configName: string;
  success: boolean;
  parlays: number;
  legs: number;
  tiers: Record<string, number>;
  durationMs: number;
  generatedAt: string;
  tokenUsage?: {
    inputTokens: number;
    outputTokens: number;
  };
  error?: string;
  picks?: Array<{
    name: string;
    legs: Array<{
      playerName: string;
      propType: string;
      line: number;
      direction: string;
      team?: string;
      opponentTeam?: string;
      homeTeam?: string;
      awayTeam?: string;
      gameStartsAt?: string;
    }>;
  }>;
}

interface UseGeneratorResult {
  // Config state
  configs: GeneratorConfig[];
  categories: Record<string, GeneratorConfig[]>;
  loadingConfigs: boolean;

  // Games state
  games: GeneratorGame[];
  loadingGames: boolean;

  // Run state
  selectedConfig: string | null;
  setSelectedConfig: (config: string | null) => void;
  analysisMode: AnalysisMode;
  setAnalysisMode: (value: AnalysisMode) => void;
  running: boolean;
  results: GeneratorResult[];

  // Loop state
  loopCount: number;
  setLoopCount: (count: number) => void;
  loopRunning: boolean;
  loopProgress: { current: number; total: number } | null;
  stopLoop: () => void;

  // Actions
  runGenerator: () => Promise<void>;
  runGeneratorLoop: () => Promise<void>;
  clearResults: () => void;
  clearErrors: () => void;
  refreshGames: () => Promise<void>;
  resetState: () => void;
}

// =============================================================================
// Hook
// =============================================================================

export function useGenerator(): UseGeneratorResult {
  // User preferences for sportsbook and authenticated fetch
  const { user, authFetch } = useAuth();
  const leagueKey = useCurrentLeagueKey();
  const sportsbook = sportsbookForLeague(user?.preferences, leagueKey);

  // Config state
  const [configs, setConfigs] = useState<GeneratorConfig[]>([]);
  const [categories, setCategories] = useState<Record<string, GeneratorConfig[]>>({});
  const [loadingConfigs, setLoadingConfigs] = useState(true);

  // Games state
  const [games, setGames] = useState<GeneratorGame[]>([]);
  const [loadingGames, setLoadingGames] = useState(true);

  // Run state - persist selected config to session storage
  const [selectedConfig, setSelectedConfigState] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem(SELECTED_CONFIG_STORAGE_KEY);
    } catch {
      return null;
    }
  });

  // Wrapper to persist selected config
  const setSelectedConfig = useCallback((config: string | null) => {
    setSelectedConfigState(config);
    try {
      if (config) {
        sessionStorage.setItem(SELECTED_CONFIG_STORAGE_KEY, config);
      } else {
        sessionStorage.removeItem(SELECTED_CONFIG_STORAGE_KEY);
      }
    } catch {
      // Ignore storage errors
    }
  }, []);
  const [analysisMode, setAnalysisMode] = useState<AnalysisMode>('standard');
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState<GeneratorResult[]>(() => loadPersistedResults());

  // Loop state
  const [loopCount, setLoopCount] = useState(3);
  const [loopRunning, setLoopRunning] = useState(false);
  const [loopProgress, setLoopProgress] = useState<{
    current: number;
    total: number;
  } | null>(null);
  const shouldStopLoopRef = useRef(false);
  // Track if the loop is actively running in this hook instance
  const loopActiveRef = useRef(false);

  // On mount, check for stale loop state and clear it
  // This handles the case where user navigated away mid-loop and the loop died
  useEffect(() => {
    const persistedState = loadPersistedLoopState();
    if (persistedState?.loopRunning && !loopActiveRef.current) {
      // There's persisted loop state but no active loop in this instance
      // This means the loop was interrupted (page refresh, etc.) - clear the stale state
      persistLoopState(null);
    }
  }, []);

  // Persist results to sessionStorage whenever they change
  useEffect(() => {
    persistResults(results);
  }, [results]);

  // Fetch configs on mount
  useEffect(() => {
    async function fetchConfigs() {
      try {
        const response = await authFetch('/api/lab/generator/configs');
        if (response.ok) {
          const data = await response.json();
          setConfigs(data.configs || []);
          setCategories(data.categories || {});
        }
      } catch (_err) {
      } finally {
        setLoadingConfigs(false);
      }
    }

    fetchConfigs();
  }, [authFetch]);

  // Fetch games on mount
  const refreshGames = useCallback(async () => {
    setLoadingGames(true);
    try {
      const response = await authFetch(
        `/api/lab/generator/games?leagueKey=${encodeURIComponent(leagueKey)}`,
      );
      if (response.ok) {
        const data = await response.json();
        setGames(data.games || []);
      }
    } catch (_err) {
    } finally {
      setLoadingGames(false);
    }
  }, [authFetch, leagueKey]);

  useEffect(() => {
    refreshGames();
  }, [refreshGames]);

  // Run generator
  const runGenerator = useCallback(async () => {
    if (!selectedConfig || running) return;

    setRunning(true);

    try {
      const response = await authFetch('/api/lab/generator/run', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          configName: selectedConfig,
          analysisMode,
          sportsbook,
          leagueKey,
        }),
      });

      const result: GeneratorResult = await response.json();

      // Add to results (most recent first)
      setResults((prev) => [result, ...prev]);
    } catch (err) {
      // Add error result
      setResults((prev) => [
        {
          configName: selectedConfig,
          success: false,
          parlays: 0,
          legs: 0,
          tiers: {},
          durationMs: 0,
          generatedAt: new Date().toISOString(),
          error: err instanceof Error ? err.message : 'Unknown error',
        },
        ...prev,
      ]);
    } finally {
      setRunning(false);
    }
  }, [selectedConfig, analysisMode, running, sportsbook, leagueKey, authFetch]);

  // Clear results (also clears sessionStorage)
  const clearResults = useCallback(() => {
    setResults([]);
    try {
      sessionStorage.removeItem(RESULTS_STORAGE_KEY);
    } catch (_err) {}
  }, []);

  // Clear only error results (keeps successful ones)
  const clearErrors = useCallback(() => {
    setResults((prev) => prev.filter((r) => r.success));
  }, []);

  // Stop loop
  const stopLoop = useCallback(() => {
    shouldStopLoopRef.current = true;
    // Note: The actual state cleanup happens in the finally block of runGeneratorLoop
    // Setting this flag will cause the loop to break on next iteration check
  }, []);

  // Reset state - emergency recovery if state gets stuck
  const resetState = useCallback(() => {
    setRunning(false);
    setLoopRunning(false);
    setLoopProgress(null);
    shouldStopLoopRef.current = false;
    loopActiveRef.current = false;
    persistLoopState(null);
  }, []);

  // Run generator loop - runs the generator multiple times
  const runGeneratorLoop = useCallback(async () => {
    if (!selectedConfig || running || loopRunning || loopCount < 1) return;

    setLoopRunning(true);
    loopActiveRef.current = true;
    shouldStopLoopRef.current = false;
    setLoopProgress({ current: 0, total: loopCount });

    // Persist loop state so we can detect stale state on remount
    persistLoopState({
      loopRunning: true,
      loopProgress: { current: 0, total: loopCount },
      selectedConfig,
    });

    try {
      for (let i = 0; i < loopCount; i++) {
        // Check if we should stop
        if (shouldStopLoopRef.current) {
          break;
        }

        const currentProgress = { current: i + 1, total: loopCount };
        setLoopProgress(currentProgress);
        persistLoopState({
          loopRunning: true,
          loopProgress: currentProgress,
          selectedConfig,
        });
        setRunning(true);

        try {
          const response = await authFetch('/api/lab/generator/run', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              configName: selectedConfig,
              analysisMode,
              sportsbook,
              leagueKey,
            }),
          });

          const result: GeneratorResult = await response.json();

          // Add to results (most recent first)
          setResults((prev) => [result, ...prev]);
        } catch (err) {
          // Add error result
          setResults((prev) => [
            {
              configName: selectedConfig,
              success: false,
              parlays: 0,
              legs: 0,
              tiers: {},
              durationMs: 0,
              generatedAt: new Date().toISOString(),
              error: err instanceof Error ? err.message : 'Unknown error',
            },
            ...prev,
          ]);
        }

        setRunning(false);

        // Wait between iterations to avoid rate limiting
        if (i < loopCount - 1 && !shouldStopLoopRef.current) {
          await new Promise((resolve) => setTimeout(resolve, LOOP_DELAY_MS));
        }
      }
    } finally {
      // Always clean up loop state, even if an error occurred
      setLoopRunning(false);
      setLoopProgress(null);
      setRunning(false);
      shouldStopLoopRef.current = false;
      loopActiveRef.current = false;
      persistLoopState(null);
    }
  }, [
    selectedConfig,
    analysisMode,
    loopCount,
    running,
    loopRunning,
    sportsbook,
    leagueKey,
    authFetch,
  ]);

  return {
    configs,
    categories,
    loadingConfigs,
    games,
    loadingGames,
    selectedConfig,
    setSelectedConfig,
    analysisMode,
    setAnalysisMode,
    running,
    results,
    loopCount,
    setLoopCount,
    loopRunning,
    loopProgress,
    stopLoop,
    runGenerator,
    runGeneratorLoop,
    clearResults,
    clearErrors,
    refreshGames,
    resetState,
  };
}
